import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Library } from 'lucide-react';
import { openComposeInNewTab, type ComposeReturnTarget } from '../utils/composeNavigation';
import { getNewMessageMultiWindowHintTitle } from './ComposeMultiWindowHint';
import {
  MailboxFolderIcon,
  mailboxFolderRowDefault,
  mailboxFolderRowSelected,
  type MailboxFolderAccent,
} from './mailboxFolderAccent';
import {
  SearchFolderSidebarIcon,
  accentFromSearchFolderKind,
  searchFolderRowDefault,
  searchFolderRowSelected,
} from './searchFolderSidebarIcon';
import { useTheme, type ThemeColors } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { sortSearchFolders } from '../../search-folders/sortSearchFolders';
import { normalizeSearchFolderKind } from '../../search-folders/useData';
import { Z_INDEX, zStyle } from '../../../lib/zLayers';
import { FlyoutRailBackdrop, FlyoutRailPanel } from '../../../components/ui/FlyoutFromRail';
import { CollapsedRailBody, CollapsedRailHeader, CollapsedRailShell } from '../../../components/ui/CollapsedRail';
import { RailIconButton } from '../../../components/ui/RailIconButton';
import { COLLAPSED_RAIL_WIDTH_PX } from '../../../lib/layoutConstants';
import type {
  EmailSearchFolder,
  EmailPersonalFolderTreeNode,
  EmailTag,
  SharedFolderTreeNode,
  SharedSearchFolderItem,
  SharedAccountItem,
  UnreadCounts,
  EmailImapFolder,
  AccountMailboxFolder,
} from '../useData';
import ManageTagsModal from './ManageTagsModal';
import PersonalFolderOptionsModal from './PersonalFolderOptionsModal';
import { buildImapFolderTree, type ImapFolderTreeNode } from '../utils/imapFolderTree';
import type { EmailSpecialView } from '../utils/emailSidebarSelectionStorage';
import {
  loadEmailSidebarExpanded,
  saveEmailSidebarExpanded,
} from '../utils/emailSidebarExpandedStorage';
import { searchFolderPeriodShortLabel } from '../utils/receivedDatePresets';

const ChevronDown = (
  <svg className="w-4 h-4 flex-shrink-0 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);
const ChevronRight = (
  <svg className="w-4 h-4 flex-shrink-0 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);
const ChevronLeft = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const InboxIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
  </svg>
);

/** Collapsed rail: open folder tree flyout (aligned to rail right edge) */
const FolderPanelIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h10" />
  </svg>
);

const AccountIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
  </svg>
);

/** Readable text (light/dark) on arbitrary hex background for account color badges */
function contrastTextForHexBackground(hex: string): string {
  let h = hex.trim();
  if (!h.startsWith('#')) h = `#${h}`;
  if (h.length === 4 && /^#[0-9a-fA-F]{3}$/.test(h)) {
    h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
  }
  const n = h.slice(1);
  if (n.length !== 6 || !/^[0-9a-fA-F]+$/.test(n)) return '#111827';
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? '#111827' : '#ffffff';
}

const SwatchPickerIcon = (
  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 16 16" aria-hidden>
    <circle cx="5" cy="5" r="3" />
    <circle cx="11" cy="5" r="3" className="opacity-60" />
    <circle cx="5" cy="11" r="3" className="opacity-60" />
    <circle cx="11" cy="11" r="3" className="opacity-40" />
  </svg>
);

function SearchFolderRowLabel({ name }: { name: string }) {
  return <span className="truncate flex-1 min-w-0 text-left">{name}</span>;
}

const badgeBase =
  'flex-shrink-0 min-w-[1.25rem] text-right text-xs font-medium tabular-nums rounded-full px-1.5 py-0.5';

function SearchFolderPeriodBadge({
  label,
  onSelectedBackground,
}: {
  label: string;
  onSelectedBackground?: boolean;
}) {
  return (
    <span
      className={cn(
        badgeBase,
        'max-w-[5rem] truncate text-[10px] leading-none tabular-normal',
        onSelectedBackground
          ? 'bg-white/20 text-white'
          : cn('bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'),
      )}
      title={label}
    >
      {label}
    </span>
  );
}

const FolderIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const TagIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
  </svg>
);

const GearIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const MentionsIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
  </svg>
);

const DraftIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const ArchivedIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
  </svg>
);

const SentIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const JunkIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const OutboxIcon = (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
    />
  </svg>
);

const LibraryIcon = (
  <Library className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} aria-hidden />
);

const ShareIcon = (
  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
  </svg>
);

export interface EmailAccountSidebar {
  id: number;
  emailAddress: string;
  displayName?: string | null;
  sidebarColor?: string | null;
  /** When full_mailbox, server IMAP folder tree is shown */
  imapMirrorMode?: string;
  showSentFolder?: boolean;
  showMySentFolder?: boolean;
  lastSyncAt?: string | null;
  syncStatus?: string | null;
}

function shareShowsSentFolder(share: { showSentFolder?: boolean | null }): boolean {
  return share.showSentFolder !== false;
}

function shareShowsMySentFolder(share: { showMySentFolder?: boolean | null }): boolean {
  return share.showMySentFolder !== false;
}

const MY_SENT_SIDEBAR_HINT =
  'Mail you sent through this app (any send-as identity on this account). Enable or disable per user under account sharing.';

function folderSkipsUnreadCount(path: string): boolean {
  const leaf = path.split(/[/\\]/).pop()?.trim().toLowerCase() ?? '';
  return (
    leaf === 'sent' ||
    leaf === 'my sent' ||
    leaf === 'sent items' ||
    leaf === 'sent mail' ||
    leaf === 'sent messages' ||
    leaf === 'draft' ||
    leaf === 'drafts'
  );
}

const SEARCH_FOLDERS_KEY = 'search-folders';
const PERSONAL_FOLDERS_KEY = 'personal-folders';
const TAGS_KEY = 'tags';
const SHARED_FOLDERS_KEY = 'shared-folders';
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'emails-sidebar-collapsed';
const DISPLAY_PREFS_STORAGE_KEY = 'emails-sidebar-display-preferences';

export interface EmailSidebarDisplayPrefs {
  displayNewMessageButton: boolean;
  displaySyncButton: boolean;
  displayUnreadCountBadge: boolean;
}

const DEFAULT_DISPLAY_PREFS: EmailSidebarDisplayPrefs = {
  displayNewMessageButton: true,
  displaySyncButton: true,
  displayUnreadCountBadge: true,
};

export function loadDisplayPrefs(): EmailSidebarDisplayPrefs {
  try {
    const raw = localStorage.getItem(DISPLAY_PREFS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<EmailSidebarDisplayPrefs>;
      return {
        displayNewMessageButton:
          parsed.displayNewMessageButton ?? DEFAULT_DISPLAY_PREFS.displayNewMessageButton,
        displaySyncButton: parsed.displaySyncButton ?? DEFAULT_DISPLAY_PREFS.displaySyncButton,
        displayUnreadCountBadge:
          parsed.displayUnreadCountBadge ?? DEFAULT_DISPLAY_PREFS.displayUnreadCountBadge,
      };
    }
  } catch {
    /**/
  }
  return { ...DEFAULT_DISPLAY_PREFS };
}

const ThreeDotsIcon = (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <circle cx="12" cy="6" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="12" cy="18" r="1.5" />
  </svg>
);

interface EmailSidebarProps {
  accounts: EmailAccountSidebar[];
  /** @deprecated Emails sidebar lists only search folders shared with the user. */
  searchFolders?: EmailSearchFolder[];
  personalFolders: EmailPersonalFolderTreeNode[];
  sharedFolders: SharedFolderTreeNode[];
  unreadCounts?: UnreadCounts;
  selectedAccount: number | undefined;
  /** @deprecated Use selectedSharedSearchFolderShareId instead. */
  selectedSearchFolderId?: number | undefined;
  selectedPersonalFolderId: number | undefined;
  selectedShareId: number | undefined;
  onSelectInbox: (accountId: number, folder?: AccountMailboxFolder) => void;
  /** @deprecated Use onSelectSharedSearchFolder instead. */
  onSelectSearchFolder?: (folder: EmailSearchFolder) => void;
  onSelectPersonalFolder: (folder: EmailPersonalFolderTreeNode | null) => void;
  onSelectSharedFolder: (share: SharedFolderTreeNode | null) => void;
  onCreatePersonalFolder?: (parentId?: number) => void;
  onShareFolder?: (folder: EmailPersonalFolderTreeNode) => void;
  onRenamePersonalFolder?: (folderId: number, name: string) => Promise<void>;
  onMovePersonalFolder?: (folderId: number, parentId: number | null) => Promise<void>;
  onAddSubfolder?: (folder: EmailPersonalFolderTreeNode) => void;
  onDeletePersonalFolder?: (folderId: number) => Promise<void>;
  /** When user drops an email onto a personal folder */
  onDropOnFolder?: (folderId: number, emailId: number, partitionAt?: string | null) => void;
  /** Reorder root-level personal folders (folderIds = new order of root IDs) */
  onReorderPersonalFolders?: (folderIds: number[]) => Promise<void>;
  sharedSearchFolders?: SharedSearchFolderItem[];
  sharedAccounts?: SharedAccountItem[];
  selectedSharedSearchFolderShareId?: number;
  selectedSharedAccountShareId?: number;
  /** Folder selected for the shared account (Inbox, Draft, Sent, My sent) */
  selectedSharedAccountFolder?: AccountMailboxFolder;
  onSelectSharedSearchFolder?: (share: SharedSearchFolderItem | null) => void;
  onSelectSharedAccount?: (share: SharedAccountItem | null, folder?: AccountMailboxFolder) => void;
  /** Notify parent when collapsed state changes (so layout can shrink the left column) */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** When set, sidebar collapse is controlled by the parent (persisted UI prefs). */
  sidebarCollapsed?: boolean;
  /** Display preferences (new message button, sync, badges). If not provided, sidebar uses internal state + localStorage. */
  displayPrefs?: EmailSidebarDisplayPrefs;
  onDisplayPrefsChange?: (prefs: EmailSidebarDisplayPrefs) => void;
  /** Called when user clicks Refresh in the sidebar header (revalidate list) */
  onRefresh?: () => void | Promise<void>;
  /** Email tags for filtering; select a tag to filter messages */
  emailTags?: EmailTag[];
  selectedTagId?: number;
  onSelectTag?: (tagId: number | undefined) => void;
  onCreateTag?: (name: string, color?: string | null) => Promise<EmailTag>;
  onDeleteTag?: (tagId: number) => Promise<void>;
  onReorderTags?: (tagIds: number[]) => Promise<unknown>;
  onShareTag?: (tag: EmailTag) => void;
  /** Set sidebar color for an account (user preference). Only for own accounts. */
  onAccountSidebarColorChange?: (accountId: number, color: string | null) => void | Promise<void>;
  /** When viewing Draft or Templates list (messages created as draft or saved as template). */
  selectedSpecialView?: EmailSpecialView | null;
  onSelectSpecialView?: (view: EmailSpecialView | null) => void;
  /** Mentions inbox — emails where the current user was @mentioned in collaboration */
  mentionsViewActive?: boolean;
  mentionUnreadCount?: number;
  onSelectMentionsView?: (active: boolean) => void;
  /** Cached IMAP LIST folders per account id (full_mailbox) */
  imapFoldersByAccount?: Record<number, EmailImapFolder[]>;
  /** Current IMAP path when an own account is selected (e.g. INBOX, [Gmail]/Sent) */
  selectedImapFolderPath?: string;
  onSelectImapFolder?: (accountId: number, imapPath: string) => void;
  /**
   * Kept for API compatibility. "New message" now opens compose in its own browser tab,
   * so there is no in-app return target to carry.
   */
  composeReturnTo?: ComposeReturnTarget;
  /** When set, enables "Mark folder as read — {name}" in the 3-dot menu. */
  markFolderAsReadLabel?: string | null;
  onMarkFolderAsRead?: () => void;
}

/** Preset hex colors for email tag color picker */
export const EMAIL_TAG_COLORS = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899',
  '#64748b', null as string | null,
];

/** Preset colors for account sidebar (name only) */
const ACCOUNT_SIDEBAR_COLORS = ['#ef4444', '#f97316', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#64748b'];

function unreadBadgeClass(colors: ThemeColors): string {
  return cn(badgeBase, colors.primary, 'bg-opacity-20 dark:bg-opacity-30', colors.primaryFg);
}

function totalBadgeClass(colors: ThemeColors): string {
  return cn(badgeBase, colors.surfaceHover, colors.textSecondary);
}

function UnreadBadge({
  count,
  className,
  colors: colorsProp,
  onSelectedBackground = false,
  label = 'unread',
}: {
  count: number;
  className?: string;
  colors?: ThemeColors;
  /** Use high-contrast styling when the parent row has the primary selected background */
  onSelectedBackground?: boolean;
  /** Accessible name for the number. Sent smart folders show the visible message count. */
  label?: string;
}) {
  const { colors: themeColors } = useTheme();
  const colors = colorsProp ?? themeColors;
  const badgeClass = onSelectedBackground
    ? cn(badgeBase, 'bg-white/25 text-white font-semibold')
    : unreadBadgeClass(colors);
  const text = `${count.toLocaleString()} ${label}`;
  return (
    <span className={className ?? badgeClass} title={text} aria-label={text}>
      {count.toLocaleString()}
    </span>
  );
}

/** Sent and My sent smart folders show how many messages are in the list, not unread. */
function searchFolderBadgeUsesVisibleTotal(kind?: string | null): boolean {
  const normalized = normalizeSearchFolderKind(kind);
  return (
    normalized === 'sent' ||
    normalized === 'combinedSent' ||
    normalized === 'mySent' ||
    normalized === 'combinedMySent'
  );
}

function TotalBadge({
  count,
  className,
  colors: colorsProp,
  onSelectedBackground = false,
}: {
  count: number;
  className?: string;
  colors?: ThemeColors;
  onSelectedBackground?: boolean;
}) {
  const { colors: themeColors } = useTheme();
  const colors = colorsProp ?? themeColors;
  const badgeClass = onSelectedBackground
    ? cn(badgeBase, 'bg-white/15 text-white/95')
    : totalBadgeClass(colors);
  return (
    <span className={className ?? badgeClass} title={`${count} total`} aria-label={`${count} total`}>
      {count.toLocaleString()}
    </span>
  );
}

/** Recursive tree row for a personal folder node */
function PersonalFolderTree({
  node,
  depth,
  isSelected,
  isExpanded,
  onToggleExpand,
  onSelect,
  onShareFolder,
  onOpenFolderOptions,
  onDropOnFolder,
  rootSiblingIds,
  onReorderRoot,
  rowBase,
  rowLevel1,
  rowSelected,
  rowDefault,
  colors,
  FolderIcon,
  ChevronDown,
  ChevronRight,
  GearIcon,
  getUnreadCount,
  showUnreadBadge,
  showTotalBadge,
}: {
  node: EmailPersonalFolderTreeNode;
  depth: number;
  isSelected: (id: number) => boolean;
  isExpanded: (id: number) => boolean;
  onToggleExpand: (id: number) => void;
  onSelect: (folder: EmailPersonalFolderTreeNode | null) => void;
  onShareFolder?: (folder: EmailPersonalFolderTreeNode) => void;
  onOpenFolderOptions?: (folder: EmailPersonalFolderTreeNode) => void;
  onDropOnFolder?: (folderId: number, emailId: number, partitionAt?: string | null) => void;
  /** Root-level reorder: ordered list of root folder IDs */
  rootSiblingIds?: number[];
  onReorderRoot?: (folderIds: number[]) => void | Promise<void>;
  rowBase: string;
  rowLevel1: string;
  rowSelected: string;
  rowDefault: string;
  colors: ThemeColors;
  FolderIcon: JSX.Element;
  ChevronDown: JSX.Element;
  ChevronRight: JSX.Element;
  GearIcon: JSX.Element;
  getUnreadCount?: (folderId: number) => number;
  showUnreadBadge?: boolean;
  showTotalBadge?: boolean;
}) {
  const hasChildren = node.children && node.children.length > 0;
  const expanded = isExpanded(node.id);
  const buttonPl = 3 + depth * 3;
  const isRootReorderable = depth === 0 && rootSiblingIds != null && onReorderRoot != null;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    const folderIdStr = e.dataTransfer.getData('application/x-ums-personal-folder-id');
    if (folderIdStr && isRootReorderable) {
      e.dataTransfer.dropEffect = 'move';
      return;
    }
    if (onDropOnFolder) e.dataTransfer.dropEffect = 'copy';
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const folderIdStr = e.dataTransfer.getData('application/x-ums-personal-folder-id');
    if (folderIdStr && isRootReorderable && rootSiblingIds && onReorderRoot) {
      const dragId = parseInt(folderIdStr, 10);
      if (!Number.isNaN(dragId) && dragId !== node.id) {
        const ids = [...rootSiblingIds];
        const fromIdx = ids.indexOf(dragId);
        const toIdx = ids.indexOf(node.id);
        if (fromIdx >= 0 && toIdx >= 0) {
          const [removed] = ids.splice(fromIdx, 1);
          ids.splice(toIdx, 0, removed);
          void onReorderRoot(ids);
        }
      }
      return;
    }
    const idStr = e.dataTransfer.getData('application/x-ums-email-id');
    if (idStr && onDropOnFolder) {
      const emailId = parseInt(idStr, 10);
      const partitionAtRaw = e.dataTransfer.getData('application/x-ums-email-partition-at');
      const partitionAt = partitionAtRaw || undefined;
      if (!Number.isNaN(emailId)) onDropOnFolder(node.id, emailId, partitionAt);
    }
  };
  const handleDragStart = (e: React.DragEvent) => {
    if (isRootReorderable) {
      e.dataTransfer.setData('application/x-ums-personal-folder-id', String(node.id));
      e.dataTransfer.effectAllowed = 'move';
    }
  };

  return (
    <div className="mt-0" role="treeitem" aria-expanded={hasChildren ? expanded : undefined} aria-selected={isSelected(node.id)}>
      <div
        className={cn('flex items-center min-w-0', onDropOnFolder && 'rounded', isRootReorderable && 'cursor-grab active:cursor-grabbing')}
        draggable={isRootReorderable}
        onDragStart={isRootReorderable ? handleDragStart : undefined}
        onDragOver={onDropOnFolder || isRootReorderable ? handleDragOver : undefined}
        onDrop={onDropOnFolder || isRootReorderable ? handleDrop : undefined}
        onDragEnd={() => {}}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(node.id);
            }}
            className={cn(
              'flex-shrink-0 p-0.5 rounded -ml-0.5 -mr-0.5',
              colors.textMuted,
              colors.interactiveHover
            )}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? ChevronDown : ChevronRight}
          </button>
        ) : (
          <span className="w-5 flex-shrink-0 inline-block" aria-hidden />
        )}
        <button
          type="button"
          onClick={() => onSelect(node)}
          className={cn(
            rowBase,
            rowLevel1,
            'flex-1 min-w-0',
            isSelected(node.id) ? rowSelected : rowDefault
          )}
          style={{ paddingLeft: buttonPl }}
          title={node.name}
          tabIndex={-1}
        >
          {FolderIcon}
          <span className="truncate flex-1">{node.name}</span>
          <span className="flex items-center gap-1 flex-shrink-0">
            {showUnreadBadge !== false && getUnreadCount && (
              <UnreadBadge
                count={getUnreadCount(node.id)}
                colors={colors}
                onSelectedBackground={isSelected(node.id)}
              />
            )}
            {showTotalBadge !== false && (
              <TotalBadge
                count={typeof node.entryCount === 'number' ? node.entryCount : 0}
                onSelectedBackground={isSelected(node.id)}
              />
            )}
          </span>
        </button>
        {onOpenFolderOptions && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenFolderOptions(node);
            }}
            className={cn('flex-shrink-0 p-1 rounded opacity-70', colors.textMuted, colors.interactiveHover)}
            title="Folder options"
            aria-label="Folder options"
          >
            {GearIcon}
          </button>
        )}
      </div>
      {hasChildren && expanded && (
        <div className={cn('border-l ml-3', colors.border)} role="group">
          {node.children!.map((child) => (
            <PersonalFolderTree
              key={child.id}
              node={child}
              depth={depth + 1}
              isSelected={isSelected}
              isExpanded={isExpanded}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
              onShareFolder={onShareFolder}
              onOpenFolderOptions={onOpenFolderOptions}
              onDropOnFolder={onDropOnFolder}
              rowBase={rowBase}
              rowLevel1={rowLevel1}
              rowSelected={rowSelected}
              rowDefault={rowDefault}
              colors={colors}
              FolderIcon={FolderIcon}
              ChevronDown={ChevronDown}
              ChevronRight={ChevronRight}
              GearIcon={GearIcon}
              getUnreadCount={getUnreadCount}
              showUnreadBadge={showUnreadBadge}
              showTotalBadge={showTotalBadge}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Recursive IMAP folder tree (LIST hierarchy), styled like personal folder tree */
function ImapFolderTree({
  accountId,
  nodes,
  depth,
  isImapPathSelected,
  onSelectImapFolder,
  isImapNodeExpanded,
  onToggleImapNodeExpanded,
  rowBase,
  rowLevel1,
  rowSelectedBase,
  rowSelected,
  rowDefault,
  colors,
  getImapFolderUnread,
  getImapFolderTotal,
  showUnreadBadge = true,
  showTotalBadge = true,
  InboxIcon,
  FolderIcon,
  ChevronDown,
  ChevronRight,
}: {
  accountId: number;
  nodes: ImapFolderTreeNode[];
  depth: number;
  isImapPathSelected: (accountId: number, imapPath: string) => boolean;
  onSelectImapFolder: (accountId: number, imapPath: string) => void;
  isImapNodeExpanded: (folderId: number) => boolean;
  onToggleImapNodeExpanded: (folderId: number) => void;
  rowBase: string;
  rowLevel1: string;
  rowSelectedBase: string;
  rowSelected: string;
  rowDefault: string;
  colors: ThemeColors;
  getImapFolderUnread: (accountId: number, imapPath: string) => number;
  getImapFolderTotal: (accountId: number, imapPath: string) => number;
  showUnreadBadge?: boolean;
  showTotalBadge?: boolean;
  InboxIcon: JSX.Element;
  FolderIcon: JSX.Element;
  ChevronDown: JSX.Element;
  ChevronRight: JSX.Element;
}) {
  return (
    <>
      {nodes.map((node) => {
        const { folder } = node;
        const hasChildren = node.children.length > 0;
        const expanded = isImapNodeExpanded(folder.id);
        const buttonPl = 3 + depth * 3;
        const isInbox = folder.imapPath.toUpperCase() === 'INBOX';
        const selected = isImapPathSelected(accountId, folder.imapPath);
        return (
          <div
            key={folder.id}
            className="mt-0"
            role="treeitem"
            aria-expanded={hasChildren ? expanded : undefined}
            aria-selected={selected}
          >
            <div className="flex items-center min-w-0">
              {hasChildren ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleImapNodeExpanded(folder.id);
                  }}
                  className={cn(
                    'flex-shrink-0 p-0.5 rounded -ml-0.5 -mr-0.5',
                    colors.textMuted,
                    colors.interactiveHover
                  )}
                  aria-expanded={expanded}
                  aria-label={expanded ? 'Collapse' : 'Expand'}
                >
                  {expanded ? ChevronDown : ChevronRight}
                </button>
              ) : (
                <span className="w-5 flex-shrink-0 inline-block" aria-hidden />
              )}
              <button
                type="button"
                onClick={() => onSelectImapFolder(accountId, folder.imapPath)}
                className={cn(
                  rowBase,
                  rowLevel1,
                  'flex-1 min-w-0',
                  selected
                    ? isInbox
                      ? mailboxFolderRowSelected('inbox', rowSelectedBase)
                      : rowSelected
                    : isInbox
                      ? mailboxFolderRowDefault('inbox', rowDefault)
                      : rowDefault,
                )}
                style={{ paddingLeft: buttonPl }}
                title={folder.imapPath}
                tabIndex={-1}
              >
                {isInbox ? (
                  <MailboxFolderIcon kind="inbox" selected={selected}>
                    {InboxIcon}
                  </MailboxFolderIcon>
                ) : (
                  FolderIcon
                )}
                <span className="truncate flex-1">{folder.displayName || folder.imapPath}</span>
                <span className="flex items-center gap-1 flex-shrink-0">
                  {showUnreadBadge && !folderSkipsUnreadCount(folder.imapPath) && (
                    <UnreadBadge
                      count={getImapFolderUnread(accountId, folder.imapPath)}
                      colors={colors}
                      onSelectedBackground={selected}
                    />
                  )}
                  {showTotalBadge && (
                    <TotalBadge
                      count={getImapFolderTotal(accountId, folder.imapPath)}
                      onSelectedBackground={selected}
                    />
                  )}
                </span>
              </button>
            </div>
            {hasChildren && expanded && (
              <div className={cn('border-l ml-3', colors.border)} role="group">
                <ImapFolderTree
                  accountId={accountId}
                  nodes={node.children}
                  depth={depth + 1}
                  isImapPathSelected={isImapPathSelected}
                  onSelectImapFolder={onSelectImapFolder}
                  isImapNodeExpanded={isImapNodeExpanded}
                  onToggleImapNodeExpanded={onToggleImapNodeExpanded}
                  rowBase={rowBase}
                  rowLevel1={rowLevel1}
                  rowSelectedBase={rowSelectedBase}
                  rowSelected={rowSelected}
                  rowDefault={rowDefault}
                  colors={colors}
                  getImapFolderUnread={getImapFolderUnread}
                  getImapFolderTotal={getImapFolderTotal}
                  showUnreadBadge={showUnreadBadge}
                  showTotalBadge={showTotalBadge}
                  InboxIcon={InboxIcon}
                  FolderIcon={FolderIcon}
                  ChevronDown={ChevronDown}
                  ChevronRight={ChevronRight}
                />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

export const EmailSidebar: React.FC<EmailSidebarProps> = ({
  accounts,
  searchFolders: _searchFolders = [],
  personalFolders,
  sharedFolders,
  unreadCounts,
  selectedAccount,
  selectedSearchFolderId: _selectedSearchFolderId,
  selectedPersonalFolderId,
  selectedShareId,
  onSelectInbox,
  onSelectSearchFolder: _onSelectSearchFolder,
  onSelectPersonalFolder,
  onSelectSharedFolder,
  onCreatePersonalFolder,
  onShareFolder,
  onRenamePersonalFolder,
  onMovePersonalFolder,
  onAddSubfolder,
  onDeletePersonalFolder,
  onDropOnFolder,
  onReorderPersonalFolders,
  sharedSearchFolders = [],
  sharedAccounts = [],
  selectedSharedSearchFolderShareId,
  selectedSharedAccountShareId,
  selectedSharedAccountFolder = 'INBOX',
  onSelectSharedSearchFolder,
  onSelectSharedAccount,
  onCollapsedChange,
  sidebarCollapsed: sidebarCollapsedProp,
  displayPrefs: displayPrefsProp,
  onDisplayPrefsChange,
  onRefresh,
  emailTags = [],
  selectedTagId,
  onSelectTag,
  onCreateTag,
  onDeleteTag,
  onReorderTags,
  onShareTag,
  onAccountSidebarColorChange,
  selectedSpecialView,
  onSelectSpecialView,
  mentionsViewActive = false,
  mentionUnreadCount = 0,
  onSelectMentionsView,
  imapFoldersByAccount = {},
  selectedImapFolderPath = 'INBOX',
  onSelectImapFolder,
  markFolderAsReadLabel = null,
  onMarkFolderAsRead,
}) => {
  const { colors } = useTheme();
  const [internalDisplayPrefs, setInternalDisplayPrefs] = useState<EmailSidebarDisplayPrefs>(loadDisplayPrefs);
  const [refreshing, setRefreshing] = useState(false);
  const [accountColorPopoverId, setAccountColorPopoverId] = useState<number | null>(null);
  const accountColorPopoverRef = useRef<HTMLDivElement>(null);
  const accountColorBtnRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const [accountColorPopoverStyle, setAccountColorPopoverStyle] = useState<React.CSSProperties>({});
  const displayPrefs = displayPrefsProp ?? internalDisplayPrefs;
  const newMessageHintTitle = getNewMessageMultiWindowHintTitle();
  const sortedSharedSearchFolders = useMemo(
    () => sortSearchFolders(sharedSearchFolders),
    [sharedSearchFolders],
  );
  const activeSearchFolderPeriodLabel = useMemo(() => {
    if (selectedSharedSearchFolderShareId != null) {
      const share = sortedSharedSearchFolders.find((s) => s.shareId === selectedSharedSearchFolderShareId);
      if (share) {
        return searchFolderPeriodShortLabel(share.periodPreset, share.periodYear);
      }
    }
    return null;
  }, [selectedSharedSearchFolderShareId, sortedSharedSearchFolders]);
  const showUnreadBadge = displayPrefs.displayUnreadCountBadge;
  const showTotalBadge = false;
  const setDisplayPref = (key: keyof EmailSidebarDisplayPrefs, value: boolean) => {
    const next = { ...displayPrefs, [key]: value };
    if (onDisplayPrefsChange) onDisplayPrefsChange(next);
    else {
      setInternalDisplayPrefs(next);
      try {
        localStorage.setItem(DISPLAY_PREFS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /**/
      }
    }
  };
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement | null>(null);
  const [collapsedFlyoutOpen, setCollapsedFlyoutOpen] = useState(false);
  const [collapsedPanelLeft, setCollapsedPanelLeft] = useState(COLLAPSED_RAIL_WIDTH_PX);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number } | null>(null);
  const DISPLAY_MENU_W = 288;

  useEffect(() => {
    if (!menuOpen) return;
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [menuOpen]);

  useEffect(() => {
    if (accountColorPopoverId == null) return;
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      if (accountColorPopoverRef.current?.contains(target)) return;
      const anchor = accountColorBtnRefs.current.get(accountColorPopoverId);
      if (anchor?.contains(target)) return;
      setAccountColorPopoverId(null);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [accountColorPopoverId]);

  useEffect(() => {
    if (
      accountColorPopoverId != null &&
      !accounts.some((a) => a.id === accountColorPopoverId)
    ) {
      setAccountColorPopoverId(null);
    }
  }, [accounts, accountColorPopoverId]);

  useLayoutEffect(() => {
    if (accountColorPopoverId == null) {
      setAccountColorPopoverStyle({});
      return;
    }
    const gap = 6;
    const popoverWidth = 200;
    const estimatedH = 140;
    const update = () => {
      const el = accountColorBtnRefs.current.get(accountColorPopoverId);
      if (!el) return;
      const r = el.getBoundingClientRect();
      const left = Math.max(8, Math.min(r.left, window.innerWidth - popoverWidth - 8));
      const topBelow = r.bottom + gap;
      const fitsBelow = topBelow + estimatedH <= window.innerHeight - 8;
      const top = fitsBelow ? topBelow : Math.max(8, r.top - estimatedH - gap);
      setAccountColorPopoverStyle({
        position: 'fixed',
        left,
        top,
        zIndex: collapsedFlyoutOpen ? Z_INDEX.accountPopover : Z_INDEX.portaledDropdown,
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [accountColorPopoverId, collapsedFlyoutOpen]);

  useLayoutEffect(() => {
    if (!collapsedFlyoutOpen) return;
    const update = () => {
      const el = asideRef.current;
      setCollapsedPanelLeft(el ? el.getBoundingClientRect().right : COLLAPSED_RAIL_WIDTH_PX);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [collapsedFlyoutOpen]);

  useEffect(() => {
    if (!collapsedFlyoutOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCollapsedFlyoutOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [collapsedFlyoutOpen]);

  useEffect(() => {
    if (menuOpen && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - DISPLAY_MENU_W - 8));
      setMenuRect({ top: rect.bottom + 4, left });
      const onScrollResize = () => {
        if (menuButtonRef.current) {
          const r = menuButtonRef.current.getBoundingClientRect();
          const l = Math.max(8, Math.min(r.left, window.innerWidth - DISPLAY_MENU_W - 8));
          setMenuRect({ top: r.bottom + 4, left: l });
        }
      };
      window.addEventListener('scroll', onScrollResize, true);
      window.addEventListener('resize', onScrollResize);
      return () => {
        window.removeEventListener('scroll', onScrollResize, true);
        window.removeEventListener('resize', onScrollResize);
      };
    } else {
      setMenuRect(null);
    }
  }, [menuOpen]);

  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };
  const counts = unreadCounts ?? {
    all: 0,
    allTotal: 0,
    byAccount: {},
    byAccountTotal: {},
    byAccountFolder: {},
    byAccountFolderTotal: {},
    byAccountImapFolder: {},
    byAccountImapFolderTotal: {},
    bySearchFolder: {},
    bySearchFolderTotal: {},
    byPersonalFolder: {},
  };
  const byAccount = counts.byAccount ?? {};
  const byAccountTotal = counts.byAccountTotal ?? {};
  const byAccountFolder = counts.byAccountFolder ?? {};
  const byAccountFolderTotal = counts.byAccountFolderTotal ?? {};
  const byAccountImapFolder = counts.byAccountImapFolder ?? {};
  const byAccountImapFolderTotal = counts.byAccountImapFolderTotal ?? {};
  const bySearchFolder = counts.bySearchFolder ?? {};
  const bySearchFolderTotal = counts.bySearchFolderTotal ?? {};
  const byPersonalFolder = counts.byPersonalFolder ?? {};
  const getAccountCount = (accountId: number) => (typeof byAccount[String(accountId)] === 'number' ? byAccount[String(accountId)] : 0);
  const getAccountTotal = (accountId: number) => (typeof byAccountTotal[String(accountId)] === 'number' ? byAccountTotal[String(accountId)] : 0);
  const getImapFolderUnread = (accountId: number, imapPath: string) =>
    typeof byAccountImapFolder[String(accountId)]?.[imapPath] === 'number'
      ? byAccountImapFolder[String(accountId)][imapPath]
      : 0;
  const getImapFolderTotal = (accountId: number, imapPath: string) =>
    typeof byAccountImapFolderTotal[String(accountId)]?.[imapPath] === 'number'
      ? byAccountImapFolderTotal[String(accountId)][imapPath]
      : 0;
  const getAccountFolderCount = (accountId: number, folder: AccountMailboxFolder) =>
    (typeof byAccountFolder[`${accountId}:${folder}`] === 'number' ? byAccountFolder[`${accountId}:${folder}`] : 0);
  const getAccountFolderTotal = (accountId: number, folder: AccountMailboxFolder) =>
    (typeof byAccountFolderTotal[`${accountId}:${folder}`] === 'number' ? byAccountFolderTotal[`${accountId}:${folder}`] : 0);
  const getSearchFolderCount = (id: number) => (typeof bySearchFolder[String(id)] === 'number' ? bySearchFolder[String(id)] : 0);
  const getSearchFolderTotal = (id: number) => (typeof bySearchFolderTotal[String(id)] === 'number' ? bySearchFolderTotal[String(id)] : 0);
  const getPersonalFolderUnread = (id: number) => (typeof byPersonalFolder[String(id)] === 'number' ? byPersonalFolder[String(id)] : 0);

  const isInboxSelected = (accountId: number) => isAccountFolderSelected(accountId, 'INBOX');
  const isAccountFolderSelected = (accountId: number, folder: AccountMailboxFolder) =>
    selectedSharedSearchFolderShareId === undefined &&
    selectedPersonalFolderId === undefined &&
    selectedShareId === undefined &&
    selectedSharedAccountShareId === undefined &&
    selectedTagId == null &&
    selectedAccount === accountId &&
    (selectedImapFolderPath ?? 'INBOX') === folder;

  const isImapPathSelected = (accountId: number, imapPath: string) =>
    selectedSharedSearchFolderShareId === undefined &&
    selectedPersonalFolderId === undefined &&
    selectedShareId === undefined &&
    selectedSharedAccountShareId === undefined &&
    selectedTagId == null &&
    selectedAccount === accountId &&
    selectedImapFolderPath === imapPath;
  const isPersonalFolderSelected = (id: number) =>
    selectedPersonalFolderId === id && selectedTagId == null;
  const isSharedFolderSelected = (shareId: number) =>
    selectedShareId === shareId && selectedTagId == null;
  const isSharedAccountFolderSelected = (shareId: number, folder: AccountMailboxFolder) =>
    selectedSharedAccountShareId === shareId &&
    selectedSharedAccountFolder === folder &&
    selectedTagId == null;

  const [internalCollapsed, setInternalCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const isCollapsedControlled = sidebarCollapsedProp !== undefined;
  const collapsed = isCollapsedControlled ? sidebarCollapsedProp : internalCollapsed;

  // Uncontrolled mode only: sync collapse to parent layout (e.g. column width). In controlled mode
  // the parent owns sidebarCollapsed — do not echo back via effect (causes infinite update loops).
  useEffect(() => {
    if (isCollapsedControlled) return;
    onCollapsedChange?.(collapsed);
  }, [collapsed, onCollapsedChange, isCollapsedControlled]);

  useEffect(() => {
    if (!collapsed) setCollapsedFlyoutOpen(false);
  }, [collapsed]);

  const toggleSidebarCollapsed = () => {
    const next = !collapsed;
    if (isCollapsedControlled) {
      onCollapsedChange?.(next);
      return;
    }
    setInternalCollapsed(next);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, next ? '1' : '0');
    } catch {
      /**/
    }
  };

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const stored = loadEmailSidebarExpanded();
    const s = new Set<string>(stored ?? []);
    accounts.forEach((a) => s.add(`account-${a.id}`));
    s.add(SEARCH_FOLDERS_KEY);
    if (personalFolders.length > 0) s.add(PERSONAL_FOLDERS_KEY);
    if (emailTags.length > 0) s.add(TAGS_KEY);
    if (sharedFolders.length > 0) s.add(SHARED_FOLDERS_KEY);
    sharedAccounts.forEach((sa) => s.add(`shared-account-${sa.shareId}`));
    return s;
  });

  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [folderOptionsFolder, setFolderOptionsFolder] = useState<EmailPersonalFolderTreeNode | null>(null);
  const personalFoldersTreeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setExpanded((prev) => {
      const next = new Set(prev);
      accounts.forEach((a) => next.add(`account-${a.id}`));
      next.add(SEARCH_FOLDERS_KEY);
      if (personalFolders.length > 0) next.add(PERSONAL_FOLDERS_KEY);
      if (emailTags.length > 0) next.add(TAGS_KEY);
      if (sharedFolders.length > 0) next.add(SHARED_FOLDERS_KEY);
      sharedAccounts.forEach((sa) => next.add(`shared-account-${sa.shareId}`));
      return next;
    });
  }, [accounts, personalFolders.length, emailTags.length, sharedFolders.length, sharedAccounts]);

  useEffect(() => {
    saveEmailSidebarExpanded(expanded);
  }, [expanded]);

  const toggleFolderExpanded = (folderId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      const key = `pf-${folderId}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isFolderExpanded = (folderId: number) => expanded.has(`pf-${folderId}`);

  const toggleImapNodeExpanded = (folderId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      const key = `imap-${folderId}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isImapNodeExpanded = (folderId: number) => expanded.has(`imap-${folderId}`);

  const toggle = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isExpanded = (key: string) => expanded.has(key);

  const rowBase = 'flex items-center gap-2 w-full text-left text-sm rounded-md transition-colors border-l-2 border-transparent';
  const rowLevel0 = 'py-1 pl-1 pr-2';
  const rowLevel1 = 'py-1 pl-5 pr-2';
  const rowSelectedBase = cn(colors.primary, colors.primaryText, 'font-medium');
  const rowSelected = cn(rowSelectedBase, 'border-l-indigo-500');
  const rowDefault = cn(colors.text, colors.interactiveHover);

  const renderAccentFolderButton = (
    kind: MailboxFolderAccent,
    selected: boolean,
    opts: {
      onClick: () => void;
      title: string;
      label: string;
      icon: React.ReactNode;
      badges?: React.ReactNode;
      level?: '0' | '1';
      className?: string;
    },
  ) => (
    <button
      type="button"
      onClick={opts.onClick}
      className={cn(
        rowBase,
        opts.level === '0' ? rowLevel0 : rowLevel1,
        opts.level === '0' && 'pl-1 w-full text-left',
        opts.className,
        selected ? mailboxFolderRowSelected(kind, rowSelectedBase) : mailboxFolderRowDefault(kind, rowDefault),
      )}
      title={opts.title}
    >
      <MailboxFolderIcon kind={kind} selected={selected}>
        {opts.icon}
      </MailboxFolderIcon>
      <span className="truncate flex-1">{opts.label}</span>
      {opts.badges}
    </button>
  );

  const accountColorPopoverAccount =
    accountColorPopoverId != null ? accounts.find((a) => a.id === accountColorPopoverId) : undefined;

  const renderSearchFoldersSection = () => (
    <div className={cn('mt-1.5 pt-1.5 border-t', colors.border)}>
      <div className="flex items-center min-w-0">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggle(SEARCH_FOLDERS_KEY);
          }}
          className={cn(
            'flex-shrink-0 p-1 rounded -ml-0.5',
            colors.textMuted,
            colors.interactiveHover
          )}
          aria-expanded={isExpanded(SEARCH_FOLDERS_KEY)}
          aria-label={isExpanded(SEARCH_FOLDERS_KEY) ? 'Collapse Smart Folders' : 'Expand Smart Folders'}
        >
          {isExpanded(SEARCH_FOLDERS_KEY) ? ChevronDown : ChevronRight}
        </button>
        <span className={cn('py-1 pl-2 text-[11px] font-semibold uppercase tracking-wider flex-1', colors.textMuted)}>
          Smart Folders
        </span>
      </div>
      {isExpanded(SEARCH_FOLDERS_KEY) && (
        <div>
          {sortedSharedSearchFolders.length > 0 ? (
            sortedSharedSearchFolders.map((share) => {
                const periodLabel = searchFolderPeriodShortLabel(share.periodPreset, share.periodYear);
                const selected =
                  selectedSharedSearchFolderShareId === share.shareId && selectedTagId == null;
                const accent = accentFromSearchFolderKind(share.folderKind);
                return (
                <button
                  key={share.shareId}
                  type="button"
                  onClick={() => onSelectSharedSearchFolder?.(share)}
                  className={cn(
                    rowBase,
                    rowLevel0,
                    'mt-0.5',
                    selected
                      ? searchFolderRowSelected(accent, rowSelectedBase)
                      : searchFolderRowDefault(accent, rowDefault),
                  )}
                  title={
                    periodLabel
                      ? `${share.name}${share.ownerName ? ` (${share.ownerName})` : ''} · ${periodLabel}`
                      : `${share.name}${share.ownerName ? ` (${share.ownerName})` : ''}`
                  }
                >
                  <SearchFolderSidebarIcon kind={share.folderKind} selected={selected} />
                  <SearchFolderRowLabel name={share.name} />
                  <span className="flex items-center gap-1 flex-shrink-0 self-center">
                    {periodLabel && (
                      <SearchFolderPeriodBadge
                        label={periodLabel}
                        onSelectedBackground={selected}
                      />
                    )}
                    {showUnreadBadge && (
                      <UnreadBadge
                        count={
                          searchFolderBadgeUsesVisibleTotal(share.folderKind)
                            ? getSearchFolderTotal(share.folderId)
                            : getSearchFolderCount(share.folderId)
                        }
                        label={
                          searchFolderBadgeUsesVisibleTotal(share.folderKind) ? 'messages' : 'unread'
                        }
                        colors={colors}
                        onSelectedBackground={selected}
                      />
                    )}
                    {showTotalBadge && (
                      <TotalBadge
                        count={getSearchFolderTotal(share.folderId)}
                        onSelectedBackground={selected}
                      />
                    )}
                  </span>
                </button>
                );
              })
          ) : (
            <p className={cn('py-2 pl-1 text-xs', colors.textMuted)}>
              No smart folders shared with you yet. Share a folder with yourself to see it here.
            </p>
          )}
        </div>
      )}
    </div>
  );

  const renderFolderTree = (): React.ReactNode => (
    <>
        {/* Accounts (expandable) */}
        {accounts.map((acc) => {
          const key = `account-${acc.id}`;
          const expandedAccount = isExpanded(key);
          return (
            <div key={key} className="mt-0.5">
              <div className="flex items-center min-w-0">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(key);
                  }}
                  className={cn(
                    'flex-shrink-0 p-0.5 rounded',
                    colors.textMuted,
                    colors.interactiveHover
                  )}
                  aria-expanded={expandedAccount}
                  aria-label={expandedAccount ? 'Collapse' : 'Expand'}
                >
                  {expandedAccount ? ChevronDown : ChevronRight}
                </button>
                <div className="flex-1 min-w-0 flex items-center relative">
                  <button
                    type="button"
                    onClick={() => onSelectInbox(acc.id)}
                    className={cn(rowBase, rowLevel0, 'flex-1 min-w-0 gap-1.5', rowDefault)}
                    title={acc.displayName?.trim() ? `${acc.displayName} (${acc.emailAddress})` : acc.emailAddress}
                  >
                    {!acc.sidebarColor ? (
                      <>
                        {AccountIcon}
                        <span className="truncate flex-1 text-xs text-left">{acc.emailAddress}</span>
                      </>
                    ) : (
                      <span
                        className={cn(
                          'truncate min-w-0 flex-1 text-left rounded-md px-2 py-0.5 text-xs font-semibold',
                          'shadow-sm ring-1 ring-black/10 dark:ring-white/20'
                        )}
                        style={{
                          backgroundColor: acc.sidebarColor,
                          color: contrastTextForHexBackground(acc.sidebarColor),
                        }}
                      >
                        {acc.displayName?.trim() || acc.emailAddress}
                      </span>
                    )}
                  </button>
                  {onAccountSidebarColorChange && (
                    <>
                      <button
                        type="button"
                        ref={(el) => {
                          if (el) accountColorBtnRefs.current.set(acc.id, el);
                          else accountColorBtnRefs.current.delete(acc.id);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAccountColorPopoverId(accountColorPopoverId === acc.id ? null : acc.id);
                        }}
                        className={cn(
                          'flex-shrink-0 h-7 w-7 rounded-md ml-0.5 flex items-center justify-center',
                          colors.border,
                          colors.interactiveHover,
                          colors.textMuted
                        )}
                        title="Account color"
                        aria-label="Set account color"
                      >
                        {SwatchPickerIcon}
                      </button>
                    </>
                  )}
                </div>
              </div>
              {expandedAccount && (
                <div>
                  {acc.imapMirrorMode === 'full_mailbox' &&
                  (imapFoldersByAccount[acc.id]?.length ?? 0) > 0 &&
                  onSelectImapFolder ? (
                    <div
                      className="ml-0.5 mt-0"
                      role="tree"
                      aria-label={`IMAP folders for ${acc.emailAddress ?? 'account'}`}
                    >
                      <ImapFolderTree
                        accountId={acc.id}
                        nodes={buildImapFolderTree(imapFoldersByAccount[acc.id] ?? [])}
                        depth={0}
                        isImapPathSelected={isImapPathSelected}
                        onSelectImapFolder={onSelectImapFolder}
                        isImapNodeExpanded={isImapNodeExpanded}
                        onToggleImapNodeExpanded={toggleImapNodeExpanded}
                        rowBase={rowBase}
                        rowLevel1={rowLevel1}
                        rowSelectedBase={rowSelectedBase}
                        rowSelected={rowSelected}
                        rowDefault={rowDefault}
                        colors={colors}
                        getImapFolderUnread={getImapFolderUnread}
                        getImapFolderTotal={getImapFolderTotal}
                        showUnreadBadge={showUnreadBadge}
                        showTotalBadge={showTotalBadge}
                        InboxIcon={InboxIcon}
                        FolderIcon={FolderIcon}
                        ChevronDown={ChevronDown}
                        ChevronRight={ChevronRight}
                      />
                    </div>
                  ) : (
                    <>
                      {renderAccentFolderButton('inbox', isAccountFolderSelected(acc.id, 'INBOX'), {
                        onClick: () => onSelectInbox(acc.id, 'INBOX'),
                        title: 'Inbox',
                        label: 'Inbox',
                        icon: InboxIcon,
                        level: '1',
                        badges: (
                          <span className="flex items-center gap-1 flex-shrink-0">
                            {showUnreadBadge && (
                              <UnreadBadge
                                count={getAccountFolderCount(acc.id, 'INBOX')}
                                colors={colors}
                                onSelectedBackground={isAccountFolderSelected(acc.id, 'INBOX')}
                              />
                            )}
                            {showTotalBadge && (
                              <TotalBadge
                                count={getAccountFolderTotal(acc.id, 'INBOX')}
                                onSelectedBackground={isAccountFolderSelected(acc.id, 'INBOX')}
                              />
                            )}
                          </span>
                        ),
                      })}
                      {shareShowsSentFolder(acc) &&
                        renderAccentFolderButton('sent', isAccountFolderSelected(acc.id, 'SENT'), {
                          onClick: () => onSelectInbox(acc.id, 'SENT'),
                          title: 'Sent (all)',
                          label: 'Sent',
                          icon: SentIcon,
                          level: '1',
                          badges: (
                            <span className="flex items-center gap-1 flex-shrink-0">
                              {showTotalBadge && (
                                <TotalBadge
                                  count={getAccountFolderTotal(acc.id, 'SENT')}
                                  onSelectedBackground={isAccountFolderSelected(acc.id, 'SENT')}
                                />
                              )}
                            </span>
                          ),
                        })}
                      {shareShowsMySentFolder(acc) &&
                        renderAccentFolderButton('mySent', isAccountFolderSelected(acc.id, 'MY_SENT'), {
                          onClick: () => onSelectInbox(acc.id, 'MY_SENT'),
                          title: MY_SENT_SIDEBAR_HINT,
                          label: 'My sent',
                          icon: SentIcon,
                          level: '1',
                          badges: (
                            <span className="flex items-center gap-1 flex-shrink-0">
                              {showTotalBadge && (
                                <TotalBadge
                                  count={getAccountFolderTotal(acc.id, 'MY_SENT')}
                                  onSelectedBackground={isAccountFolderSelected(acc.id, 'MY_SENT')}
                                />
                              )}
                            </span>
                          ),
                        })}
                      {renderAccentFolderButton('junk', isAccountFolderSelected(acc.id, 'JUNK'), {
                        onClick: () => onSelectInbox(acc.id, 'JUNK'),
                        title: 'Junk / Spam',
                        label: 'Junk',
                        icon: JunkIcon,
                        level: '1',
                        badges: (
                          <span className="flex items-center gap-1 flex-shrink-0">
                            {showUnreadBadge && (
                              <UnreadBadge
                                count={getAccountFolderCount(acc.id, 'JUNK')}
                                colors={colors}
                                onSelectedBackground={isAccountFolderSelected(acc.id, 'JUNK')}
                              />
                            )}
                            {showTotalBadge && (
                              <TotalBadge
                                count={getAccountFolderTotal(acc.id, 'JUNK')}
                                onSelectedBackground={isAccountFolderSelected(acc.id, 'JUNK')}
                              />
                            )}
                          </span>
                        ),
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Shared accounts (shared with me) - expandable with Inbox, Sent */}
        {sharedAccounts.length > 0 && (
          <div className={cn('mt-1.5 pt-1.5 border-t', colors.border)}>
            <div className={cn('py-1 pl-2 text-[10px] font-medium uppercase tracking-wider', colors.textMuted)}>
              Shared accounts
            </div>
            <div className="mt-0.5">
              {sharedAccounts.map((share) => {
                const key = `shared-account-${share.shareId}`;
                const expandedShared = isExpanded(key);
                return (
                  <div key={key} className="mt-0.5">
                    <div className="flex items-center min-w-0">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggle(key);
                        }}
                        className={cn(
                          'flex-shrink-0 p-0.5 rounded',
                          colors.textMuted,
                          colors.interactiveHover
                        )}
                        aria-expanded={expandedShared}
                        aria-label={expandedShared ? 'Collapse' : 'Expand'}
                      >
                        {expandedShared ? ChevronDown : ChevronRight}
                      </button>
                      <button
                        type="button"
                        onClick={() => onSelectSharedAccount?.(share, 'INBOX')}
                        className={cn(rowBase, rowLevel0, 'flex-1 min-w-0', rowDefault)}
                        title={`#${share.emailAddress} (${share.ownerName})`}
                      >
                        {AccountIcon}
                        <span className="truncate flex-1 text-xs">#{share.emailAddress}</span>
                      </button>
                    </div>
                    {expandedShared && (
                      <div>
                        {renderAccentFolderButton(
                          'inbox',
                          isSharedAccountFolderSelected(share.shareId, 'INBOX'),
                          {
                            onClick: () => onSelectSharedAccount?.(share, 'INBOX'),
                            title: 'Inbox',
                            label: 'Inbox',
                            icon: InboxIcon,
                            level: '1',
                            badges: (
                              <span className="flex items-center gap-1 flex-shrink-0">
                                {showUnreadBadge && (
                                  <UnreadBadge
                                    count={getAccountFolderCount(share.accountId, 'INBOX')}
                                    colors={colors}
                                    onSelectedBackground={isSharedAccountFolderSelected(share.shareId, 'INBOX')}
                                  />
                                )}
                                {showTotalBadge && (
                                  <TotalBadge
                                    count={getAccountFolderTotal(share.accountId, 'INBOX')}
                                    onSelectedBackground={isSharedAccountFolderSelected(share.shareId, 'INBOX')}
                                  />
                                )}
                              </span>
                            ),
                          },
                        )}
                        {shareShowsSentFolder(share) &&
                          renderAccentFolderButton(
                            'sent',
                            isSharedAccountFolderSelected(share.shareId, 'SENT'),
                            {
                              onClick: () => onSelectSharedAccount?.(share, 'SENT'),
                              title: 'Sent (all)',
                              label: 'Sent',
                              icon: SentIcon,
                              level: '1',
                              badges: (
                                <span className="flex items-center gap-1 flex-shrink-0">
                                  {showTotalBadge && (
                                    <TotalBadge
                                      count={getAccountFolderTotal(share.accountId, 'SENT')}
                                      onSelectedBackground={isSharedAccountFolderSelected(share.shareId, 'SENT')}
                                    />
                                  )}
                                </span>
                              ),
                            },
                          )}
                        {shareShowsMySentFolder(share) &&
                          renderAccentFolderButton(
                            'mySent',
                            isSharedAccountFolderSelected(share.shareId, 'MY_SENT'),
                            {
                              onClick: () => onSelectSharedAccount?.(share, 'MY_SENT'),
                              title: MY_SENT_SIDEBAR_HINT,
                              label: 'My sent',
                              icon: SentIcon,
                              level: '1',
                              badges: (
                                <span className="flex items-center gap-1 flex-shrink-0">
                                  {showTotalBadge && (
                                    <TotalBadge
                                      count={getAccountFolderTotal(share.accountId, 'MY_SENT')}
                                      onSelectedBackground={isSharedAccountFolderSelected(share.shareId, 'MY_SENT')}
                                    />
                                  )}
                                </span>
                              ),
                            },
                          )}
                        {renderAccentFolderButton(
                          'junk',
                          isSharedAccountFolderSelected(share.shareId, 'JUNK'),
                          {
                            onClick: () => onSelectSharedAccount?.(share, 'JUNK'),
                            title: 'Junk / Spam',
                            label: 'Junk',
                            icon: JunkIcon,
                            level: '1',
                            badges: (
                              <span className="flex items-center gap-1 flex-shrink-0">
                                {showUnreadBadge && (
                                  <UnreadBadge
                                    count={getAccountFolderCount(share.accountId, 'JUNK')}
                                    colors={colors}
                                    onSelectedBackground={isSharedAccountFolderSelected(share.shareId, 'JUNK')}
                                  />
                                )}
                                {showTotalBadge && (
                                  <TotalBadge
                                    count={getAccountFolderTotal(share.accountId, 'JUNK')}
                                    onSelectedBackground={isSharedAccountFolderSelected(share.shareId, 'JUNK')}
                                  />
                                )}
                              </span>
                            ),
                          },
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {renderSearchFoldersSection()}

        {/* Draft and Library */}
        <div className={cn('mt-1.5 pt-1.5 border-t', colors.border)}>
          {renderAccentFolderButton('mentions', mentionsViewActive === true, {
            onClick: () => onSelectMentionsView?.(!mentionsViewActive),
            title: 'Mentions',
            label: 'Mentions',
            icon: MentionsIcon,
            level: '0',
            badges:
              (mentionUnreadCount ?? 0) > 0 ? (
                <UnreadBadge count={mentionUnreadCount ?? 0} onSelectedBackground={mentionsViewActive === true} />
              ) : undefined,
          })}
          {renderAccentFolderButton('drafts', selectedSpecialView === 'drafts', {
            onClick: () => onSelectSpecialView?.(selectedSpecialView === 'drafts' ? null : 'drafts'),
            title: 'Draft',
            label: 'Draft',
            icon: DraftIcon,
            level: '0',
          })}
          {renderAccentFolderButton('archived', selectedSpecialView === 'archived', {
            onClick: () => onSelectSpecialView?.(selectedSpecialView === 'archived' ? null : 'archived'),
            title: 'Archived',
            label: 'Archived',
            icon: ArchivedIcon,
            level: '0',
            className: 'mt-0.5',
          })}
          {renderAccentFolderButton('library', selectedSpecialView === 'templates', {
            onClick: () => onSelectSpecialView?.(selectedSpecialView === 'templates' ? null : 'templates'),
            title: 'Library',
            label: 'Library',
            icon: LibraryIcon,
            level: '0',
            className: 'mt-0.5',
          })}
          {renderAccentFolderButton('outbox', selectedSpecialView === 'outbox', {
            onClick: () => onSelectSpecialView?.(selectedSpecialView === 'outbox' ? null : 'outbox'),
            title: 'Outbox',
            label: 'Outbox',
            icon: OutboxIcon,
            level: '0',
            className: 'mt-0.5',
          })}
        </div>

        {/* Shared Folders (personal folders shared with me) */}
        {sharedFolders.length > 0 && (
          <div className={cn('mt-1.5 pt-1.5 border-t', colors.border)}>
            <div className="flex items-center min-w-0">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(SHARED_FOLDERS_KEY);
                }}
                className={cn(
                  'flex-shrink-0 p-1 rounded -ml-0.5',
                  colors.textMuted,
                  colors.interactiveHover
                )}
                aria-expanded={isExpanded(SHARED_FOLDERS_KEY)}
                aria-label={isExpanded(SHARED_FOLDERS_KEY) ? 'Collapse Shared Folders' : 'Expand Shared Folders'}
              >
                {isExpanded(SHARED_FOLDERS_KEY) ? ChevronDown : ChevronRight}
              </button>
              <span className={cn('py-1 pl-2 text-[11px] font-semibold uppercase tracking-wider flex-1', colors.textMuted)}>
                Shared folders
              </span>
            </div>
            {isExpanded(SHARED_FOLDERS_KEY) && (
              <div className={cn('border-l ml-3 pl-0', colors.border)}>
                {sharedFolders.map((share) => (
                  <button
                    key={share.shareId}
                    type="button"
                    onClick={() => onSelectSharedFolder(share)}
                    className={cn(
                      rowBase,
                      rowLevel1,
                      'ml-0.5 mt-0.5',
                      isSharedFolderSelected(share.shareId) ? rowSelected : rowDefault
                    )}
                    title={`${share.name} (${share.ownerName})`}
                  >
                    {FolderIcon}
                    <span className="truncate flex-1">{share.name}</span>
                    {share.entryCount != null && (
                      <span
                        className={cn(
                          'flex-shrink-0 text-xs tabular-nums rounded-full px-1.5 py-0.5',
                          isSharedFolderSelected(share.shareId)
                            ? 'bg-white/15 text-white/95'
                            : colors.textMuted
                        )}
                      >
                        {share.entryCount.toLocaleString()}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Personal Folders (tree) */}
        <div className={cn('mt-1.5 pt-1.5 border-t', colors.border)}>
          <div className="flex items-center min-w-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggle(PERSONAL_FOLDERS_KEY);
              }}
              className={cn(
                'flex-shrink-0 p-1 rounded -ml-0.5',
                colors.textMuted,
                colors.interactiveHover
              )}
              aria-expanded={isExpanded(PERSONAL_FOLDERS_KEY)}
              aria-label={isExpanded(PERSONAL_FOLDERS_KEY) ? 'Collapse Personal Folders' : 'Expand Personal Folders'}
            >
              {isExpanded(PERSONAL_FOLDERS_KEY) ? ChevronDown : ChevronRight}
            </button>
            <span className={cn('py-1 pl-2 text-[11px] font-semibold uppercase tracking-wider flex-1', colors.textMuted)}>
              Personal Folders
            </span>
            {onCreatePersonalFolder && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onCreatePersonalFolder(undefined);
                }}
                className={cn('flex-shrink-0 p-1 rounded text-xs font-medium', colors.textMuted, colors.interactiveHover)}
                title="New folder"
                aria-label="New folder"
              >
                + New
              </button>
            )}
          </div>
          {isExpanded(PERSONAL_FOLDERS_KEY) && (
            <div
              ref={personalFoldersTreeRef}
              className={cn('border-l ml-3 pl-0', colors.border)}
              role="tree"
              aria-label="Personal folders"
              tabIndex={personalFolders.length > 0 ? 0 : undefined}
              onKeyDown={(e) => {
                if (personalFolders.length === 0) return;
                const tree = personalFoldersTreeRef.current;
                if (!tree) return;
                const items = Array.from(tree.querySelectorAll<HTMLElement>('[role="treeitem"]'));
                if (items.length === 0) return;
                const current = items.find((el) => el.contains(document.activeElement));
                const idx = current ? items.indexOf(current) : -1;
                if (e.key === 'ArrowDown' && idx < items.length - 1) {
                  e.preventDefault();
                  const next = items[idx + 1];
                  const btn = next.querySelector<HTMLButtonElement>('button[title]');
                  (btn ?? next).focus();
                } else if (e.key === 'ArrowUp' && idx > 0) {
                  e.preventDefault();
                  const prev = items[idx - 1];
                  const btn = prev.querySelector<HTMLButtonElement>('button[title]');
                  (btn ?? prev).focus();
                } else if (e.key === 'Enter' && current) {
                  e.preventDefault();
                  const btn = current.querySelector<HTMLButtonElement>('button[title]');
                  btn?.click();
                }
              }}
            >
              {personalFolders.length === 0 ? (
                <div className={cn('py-3 pl-2 pr-2', colors.textMuted)}>
                  <p className="text-xs mb-2">Create folders to organize emails.</p>
                  {onCreatePersonalFolder && (
                    <button
                      type="button"
                      onClick={() => onCreatePersonalFolder(undefined)}
                      className={cn(
                        'text-sm font-medium px-2 py-1.5 rounded-md border transition-colors',
                        colors.text,
                        'border-current opacity-80 hover:opacity-100',
                        colors.interactiveHover
                      )}
                    >
                      Create folder
                    </button>
                  )}
                </div>
              ) : (
                personalFolders.map((node) => (
                  <PersonalFolderTree
                    key={node.id}
                    node={node}
                    depth={0}
                    isSelected={isPersonalFolderSelected}
                    isExpanded={isFolderExpanded}
                    onToggleExpand={toggleFolderExpanded}
                    onSelect={onSelectPersonalFolder}
                    onShareFolder={onShareFolder}
                    onOpenFolderOptions={onShareFolder || onDeletePersonalFolder || onRenamePersonalFolder || onMovePersonalFolder ? (folder) => setFolderOptionsFolder(folder) : undefined}
                    onDropOnFolder={onDropOnFolder}
                    rootSiblingIds={personalFolders.map((n) => n.id)}
                    onReorderRoot={onReorderPersonalFolders}
                    rowBase={rowBase}
                    rowLevel1={rowLevel1}
                    rowSelected={rowSelected}
                    rowDefault={rowDefault}
                    colors={colors}
                    FolderIcon={FolderIcon}
                    ChevronDown={ChevronDown}
                    ChevronRight={ChevronRight}
                    GearIcon={GearIcon}
                    getUnreadCount={getPersonalFolderUnread}
                    showUnreadBadge={showUnreadBadge}
                    showTotalBadge={showTotalBadge}
                  />
                ))
              )}
            </div>
          )}
        </div>
        {/* Tags (filter by tag) */}
        <div className={cn('mt-1.5 pt-1.5 border-t', colors.border)}>
          <div className="flex items-center min-w-0">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggle(TAGS_KEY);
              }}
              className={cn(
                'flex-shrink-0 p-1 rounded -ml-0.5',
                colors.textMuted,
                colors.interactiveHover
              )}
              aria-expanded={isExpanded(TAGS_KEY)}
              aria-label={isExpanded(TAGS_KEY) ? 'Collapse Tags' : 'Expand Tags'}
            >
              {isExpanded(TAGS_KEY) ? ChevronDown : ChevronRight}
            </button>
            <span className={cn('py-1 pl-2 text-[11px] font-semibold uppercase tracking-wider flex-1', colors.textMuted)}>
              Tags
            </span>
            {onCreateTag && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setManageTagsOpen(true);
                }}
                className={cn(
                  'flex-shrink-0 p-1 rounded flex items-center justify-center',
                  colors.textMuted,
                  colors.interactiveHover
                )}
                title="Manage tags"
                aria-label="Manage tags"
              >
                {GearIcon}
              </button>
            )}
          </div>
          {isExpanded(TAGS_KEY) && (
            <div className={cn('border-l ml-3 pl-0', colors.border)}>
              {emailTags.length === 0 ? (
                <p className={cn('py-1 pl-2 text-xs', colors.textMuted)}>No tags yet</p>
              ) : (
                <div className="space-y-0.5 pb-1">
                  {emailTags.map((tag) => (
                    <div key={tag.id} className="flex items-center min-w-0">
                      <button
                        type="button"
                        onClick={() => onSelectTag?.(selectedTagId === tag.id ? undefined : tag.id)}
                        className={cn(
                          'flex-1 min-w-0 flex items-center gap-2 py-1.5 pl-2 pr-1 text-left rounded text-sm',
                          selectedTagId === tag.id ? rowSelected : rowDefault
                        )}
                        title={
                          tag.isOwner === false && tag.ownerName
                            ? `${tag.name} (shared by ${tag.ownerName})`
                            : tag.name
                        }
                      >
                        <span
                          className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', !tag.color && 'bg-gray-300 dark:bg-gray-600')}
                          style={tag.color ? { backgroundColor: tag.color } : undefined}
                          aria-hidden
                        />
                        <span className="truncate min-w-0 flex-1">{tag.name}</span>
                        {tag.isOwner === false && tag.ownerName && (
                          <span className={cn('text-[10px] truncate max-w-[4.5rem] flex-shrink-0', colors.textMuted)}>
                            {tag.ownerName.split(' ')[0]}
                          </span>
                        )}
                        <span className={cn('text-[11px] tabular-nums flex-shrink-0', colors.textMuted)}>
                          ({tag._count?.assignments ?? 0})
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
    </>
  );

  const collapseToggleButton = (
    <RailIconButton
      label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      onClick={toggleSidebarCollapsed}
      className={cn(colors.textMuted, 'hover:bg-black/5 dark:hover:bg-white/5')}
    >
      {collapsed ? ChevronRight : ChevronLeft}
    </RailIconButton>
  );

  const collapsedRailBody = (
    <CollapsedRailBody className={cn(colors.background)}>
      <RailIconButton
        onClick={() => openComposeInNewTab()}
        label={newMessageHintTitle}
        className={cn(colors.textMuted, 'hover:bg-black/5 dark:hover:bg-white/5')}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      </RailIconButton>
      {onRefresh && (
        <RailIconButton
          label="Refresh list"
          disabled={refreshing}
          onClick={handleRefresh}
          className={cn(colors.textMuted, 'hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50')}
        >
          {refreshing ? (
            <span className="animate-spin inline-block w-4 h-4">⟳</span>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
        </RailIconButton>
      )}
      <RailIconButton
        label={
          activeSearchFolderPeriodLabel
            ? `Open folders · folder period ${activeSearchFolderPeriodLabel}`
            : 'Open folders'
        }
        onClick={() => setCollapsedFlyoutOpen((o) => !o)}
        active={collapsedFlyoutOpen}
        activeClassName={cn(colors.primary, colors.primaryText)}
        className={cn(colors.textMuted, colors.interactiveHover)}
        aria-expanded={collapsedFlyoutOpen}
      >
        <span className="relative inline-flex">
          {FolderPanelIcon}
          {activeSearchFolderPeriodLabel && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[0.5rem] h-2 px-0.5 rounded-full bg-indigo-500 text-[7px] leading-none text-white font-semibold flex items-center justify-center"
              title={`Folder period: ${activeSearchFolderPeriodLabel}`}
              aria-hidden
            >
              ·
            </span>
          )}
        </span>
      </RailIconButton>
      <div className="relative shrink-0" ref={menuRef}>
        <RailIconButton
          ref={menuButtonRef}
          label="Display options"
          onClick={() => setMenuOpen((o) => !o)}
          className={cn(colors.textMuted, 'hover:bg-black/5 dark:hover:bg-white/5')}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
        >
          {ThreeDotsIcon}
        </RailIconButton>
      </div>
    </CollapsedRailBody>
  );

  return (
    <>
    {collapsed ? (
      <CollapsedRailShell
        shellRef={asideRef}
        className={cn('min-h-0', colors.background, colors.border)}
        aria-label="Mail folders"
        aria-expanded={false}
      >
        <CollapsedRailHeader className={colors.border}>
          {collapseToggleButton}
        </CollapsedRailHeader>
        {collapsedRailBody}
      </CollapsedRailShell>
    ) : (
    <aside
      ref={asideRef}
      className={cn(
        'flex flex-col h-full min-h-0 w-full max-w-full flex-shrink-0 border-r overflow-x-hidden overflow-y-hidden min-w-0',
        colors.background,
        colors.border
      )}
      aria-label="Mail folders"
    >
      <div
        className={cn(
          'flex flex-shrink-0 items-center border-b justify-between gap-1 px-3 py-2 min-h-[2.25rem]',
          colors.border
        )}
      >
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <button
            type="button"
            onClick={() => openComposeInNewTab()}
            className={cn(
              'flex-shrink-0 flex items-center justify-center w-8 h-8 rounded p-1 transition-colors',
              colors.textMuted,
              'hover:bg-black/5 dark:hover:bg-white/5'
            )}
            title={newMessageHintTitle}
            aria-label={newMessageHintTitle}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
          {onRefresh && (
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className={cn(
                'flex-shrink-0 flex items-center justify-center w-8 h-8 rounded transition-colors',
                colors.textMuted,
                'hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50'
              )}
              title="Refresh list"
              aria-label="Refresh list"
            >
              {refreshing ? (
                <span className="animate-spin inline-block w-4 h-4">⟳</span>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
            </button>
          )}
        </div>
        <div className="relative flex-shrink-0" ref={menuRef}>
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className={cn(
              'rounded p-1 transition-colors',
              colors.textMuted,
              'hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-1',
              colors.background,
              'focus:ring-offset-[var(--sidebar-bg)]'
            )}
            aria-label="Mail display options"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            {ThreeDotsIcon}
          </button>
        </div>
        {collapseToggleButton}
      </div>
      <nav className="rail-nav-scroll flex-1 overflow-y-auto overflow-x-hidden py-1 min-w-0 min-h-0" aria-label="Email folders">
        {renderFolderTree()}
      </nav>
    </aside>
    )}
      {(onShareFolder || onDeletePersonalFolder || onRenamePersonalFolder || onMovePersonalFolder) && (
        <PersonalFolderOptionsModal
          isOpen={folderOptionsFolder != null}
          onClose={() => setFolderOptionsFolder(null)}
          folder={folderOptionsFolder}
          personalFolders={personalFolders}
          onShare={(f) => {
            setFolderOptionsFolder(null);
            onShareFolder?.(f);
          }}
          onRename={async (id, name) => {
            await onRenamePersonalFolder?.(id, name);
          }}
          onMove={async (id, parentId) => {
            await onMovePersonalFolder?.(id, parentId);
          }}
          onAddSubfolder={onAddSubfolder}
          onDelete={async (id) => {
            if (onDeletePersonalFolder) await onDeletePersonalFolder(id);
            setFolderOptionsFolder(null);
          }}
        />
      )}
      {onCreateTag && onDeleteTag && (
        <ManageTagsModal
          isOpen={manageTagsOpen}
          onClose={() => setManageTagsOpen(false)}
          emailTags={emailTags}
          onCreateTag={onCreateTag}
          onDeleteTag={onDeleteTag}
          onReorderTags={onReorderTags}
          onShareTag={onShareTag}
        />
      )}

    {menuOpen && menuRect && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={dropdownRef}
            role="menu"
            className={cn(
              'fixed w-72 max-h-[min(20rem,70vh)] overflow-y-auto rounded-lg border shadow-xl',
              'backdrop-blur-sm',
              colors.surface,
              colors.border
            )}
            style={{
              top: menuRect.top,
              left: menuRect.left,
              ...zStyle(Z_INDEX.portaledDropdown),
            }}
          >
            <div className={cn('px-3 pt-3 pb-1.5 border-b', colors.border, colors.text)}>
              <p className={cn('text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                Mail options
              </p>
            </div>
            {onMarkFolderAsRead && (
              <div className={cn('px-2 py-1.5 border-b', colors.border)}>
                <button
                  type="button"
                  role="menuitem"
                  disabled={!markFolderAsReadLabel}
                  className={cn(
                    'group flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                    markFolderAsReadLabel
                      ? cn(colors.text, 'hover:bg-indigo-50 dark:hover:bg-indigo-950/40')
                      : cn(colors.textMuted, 'opacity-45 cursor-not-allowed'),
                  )}
                  title={
                    markFolderAsReadLabel
                      ? `Mark folder as read — ${markFolderAsReadLabel}`
                      : 'Select a mail folder first'
                  }
                  onClick={() => {
                    if (!markFolderAsReadLabel) return;
                    setMenuOpen(false);
                    onMarkFolderAsRead();
                  }}
                >
                  <span
                    className={cn(
                      'mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border',
                      markFolderAsReadLabel
                        ? 'border-indigo-200 bg-indigo-50 text-indigo-600 dark:border-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300'
                        : cn(colors.border, colors.surfaceHover),
                    )}
                    aria-hidden
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l2 2 4-4" />
                    </svg>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium leading-tight">Mark folder as read</span>
                    <span className={cn('block text-[11px] mt-0.5 truncate', colors.textMuted)}>
                      {markFolderAsReadLabel
                        ? markFolderAsReadLabel
                        : 'Select a folder in the sidebar'}
                    </span>
                  </span>
                </button>
              </div>
            )}
            <div className="px-2 py-1.5">
              <p className={cn('px-2 py-1 text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                Folder badges
              </p>
              {[
                { key: 'displayUnreadCountBadge' as const, label: 'Unread count badge', checked: displayPrefs.displayUnreadCountBadge },
              ].map(({ key, label, checked }) => (
                <button
                  key={key}
                  role="menuitemcheckbox"
                  aria-checked={checked}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
                    colors.text,
                    colors.interactiveHover
                  )}
                  onClick={() => setDisplayPref(key, !checked)}
                >
                  <span className={cn(
                    'inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-md border-2 transition-colors',
                    checked ? `${colors.primary} border-transparent` : 'border-gray-300 dark:border-gray-600'
                  )}>
                    {checked && (
                      <svg className={cn('h-3 w-3', colors.primaryText)} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  {label}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )
      : null}

    {collapsed && collapsedFlyoutOpen && typeof document !== 'undefined'
      ? createPortal(
          <>
            <FlyoutRailBackdrop onClose={() => setCollapsedFlyoutOpen(false)} />
            <FlyoutRailPanel
              left={collapsedPanelLeft}
              ariaLabel="Mail folders"
              className={cn('w-[min(20rem,calc(100vw-3.25rem))]', colors.background, colors.border)}
            >
              <div
                className={cn(
                  'flex-shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b min-h-[2.25rem]',
                  colors.border
                )}
              >
                <span className={cn('text-[10px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                  Folders
                </span>
                <button
                  type="button"
                  onClick={() => setCollapsedFlyoutOpen(false)}
                  className={cn(
                    'flex-shrink-0 rounded-md p-1.5 text-sm leading-none transition-colors',
                    colors.textMuted,
                    colors.interactiveHover
                  )}
                  aria-label="Close folders panel"
                >
                  ×
                </button>
              </div>
              <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden py-1" aria-label="Email folders">
                {renderFolderTree()}
              </nav>
            </FlyoutRailPanel>
          </>,
          document.body
        )
      : null}

    {accountColorPopoverAccount && onAccountSidebarColorChange && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={accountColorPopoverRef}
            style={accountColorPopoverStyle}
            className={cn(
              'w-[200px] rounded-lg border py-2 px-2 shadow-xl backdrop-blur-sm',
              colors.surface,
              colors.border
            )}
            role="dialog"
            aria-label="Account color"
          >
            <div className={cn('text-[10px] font-semibold uppercase tracking-wider mb-1.5', colors.textMuted)}>
              Account color
            </div>
            <div className="flex flex-wrap gap-1">
              {ACCOUNT_SIDEBAR_COLORS.map((hex) => (
                <button
                  key={hex}
                  type="button"
                  onClick={() => {
                    onAccountSidebarColorChange(accountColorPopoverAccount.id, hex);
                    setAccountColorPopoverId(null);
                  }}
                  className="w-6 h-6 rounded-full border-2 transition-transform hover:scale-110"
                  style={{
                    backgroundColor: hex,
                    borderColor: accountColorPopoverAccount.sidebarColor === hex ? 'var(--tw-ring-color, #3b82f6)' : 'transparent',
                  }}
                  title={hex}
                />
              ))}
              <button
                type="button"
                onClick={() => {
                  onAccountSidebarColorChange(accountColorPopoverAccount.id, null);
                  setAccountColorPopoverId(null);
                }}
                className={cn(
                  'text-xs px-2 py-1 rounded border',
                  colors.border,
                  colors.interactiveHover,
                  colors.text
                )}
              >
                Default
              </button>
            </div>
          </div>,
          document.body
        )
      : null}
    </>
  );
};

export default EmailSidebar;
